/* LWA Stylist intake — the capture organ (S-tier items 1–4)
   Two doors, always visible: live camera (coached) + camera roll (hardened).
   Quality gate on-device before anything uploads: blur (Laplacian variance),
   exposure (histogram clipping), framing (wall coverage), resolution.
   Exposes window.StylistIntake:
     .openRoll()  → Promise<{canvas, blob, gate}>  (null if cancelled/failed)
     .openCamera()→ Promise<{canvas, blob, gate}>  (null if cancelled)
     .gate(canvas)→ {pass, level, problems:[{why,fix}], scores}
     .scoreFrame(canvas) → {blur, exposure, framing, ok}  (live coaching reuse)
   HEIC via heic2any (lazy-loaded CDN), EXIF orientation via createImageBitmap.
*/
window.StylistIntake = (function () {
  'use strict';

  /* ————— the quality gate ————— */
  const RES_MIN = 800;          // long edge floor for an honest read
  function scoreFrame(canvas) {
    const S = 160;
    const cv = document.createElement('canvas');
    const ar = canvas.height / canvas.width;
    cv.width = S; cv.height = Math.round(S * ar);
    const g = cv.getContext('2d', { willReadFrequently: true });
    g.drawImage(canvas, 0, 0, cv.width, cv.height);
    const d = g.getImageData(0, 0, cv.width, cv.height).data;
    const W = cv.width, H = cv.height;
    const lum = new Float32Array(W * H);
    let clipHi = 0, clipLo = 0, lumSum = 0;
    for (let i = 0, p = 0; i < d.length; i += 4, p++) {
      const L = .2126 * d[i] + .7152 * d[i + 1] + .0722 * d[i + 2];
      lum[p] = L; lumSum += L;
      if (L >= 252) clipHi++; else if (L <= 4) clipLo++;
    }
    const n = W * H;
    // Laplacian variance (blur)
    let lapSum = 0, lapSq = 0, lc = 0;
    for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      const lap = 4 * lum[i] - lum[i - 1] - lum[i + 1] - lum[i - W] - lum[i + W];
      lapSum += lap; lapSq += lap * lap; lc++;
    }
    const lapMean = lapSum / lc;
    const blurVar = lapSq / lc - lapMean * lapMean;
    // framing: strong-edge share near borders vs center — a readable wall shot
    // has interior structure; a frame filled edge-to-edge with texture (too
    // close) or empty (too far) both score low
    let edgeBorder = 0, edgeMid = 0, nb = 0, nm = 0;
    for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      const e = Math.abs(lum[i] - lum[i - 1]) + Math.abs(lum[i] - lum[i - W]);
      const border = x < W * .12 || x > W * .88 || y < H * .12 || y > H * .88;
      if (border) { edgeBorder += e; nb++; } else { edgeMid += e; nm++; }
    }
    const density = (edgeBorder + edgeMid) / (nb + nm);
    const exposure = { hi: clipHi / n, lo: clipLo / n, mean: lumSum / n };
    return { blurVar, exposure, density, res: [canvas.width, canvas.height] };
  }

  function gate(canvas) {
    const s = scoreFrame(canvas);
    const problems = [];
    const caveats = [];
    const longEdge = Math.max(canvas.width, canvas.height);
    if (longEdge < RES_MIN)
      problems.push({ why: 'the photo is too small for me to read honestly — I can barely make out the wall', fix: 'use the original photo, not a thumbnail or a screenshot of one' });
    if (s.exposure.mean < 40 || s.exposure.lo > 0.35)
      problems.push({ why: 'the room is too dark in this shot to read its light and color honestly', fix: 'face the window, or turn on a lamp, and shoot again' });
    if (s.exposure.hi > 0.18 || s.exposure.mean > 235)
      problems.push({ why: 'the shot is blown out — the wall and its light are washed to white', fix: 'step out of the glare or angle away from the window' });
    if (s.blurVar < 100)
      problems.push({ why: 'the photo is too blurry for me to trust what I\'d say about it', fix: 'hold the phone steady with both hands and let it focus before the shot' });
    else if (s.blurVar < 260)
      caveats.push('a touch soft — I\'ll read it, but daylight and a steady hand sharpen the answer');
    if (s.density < 2.0)
      problems.push({ why: 'I can\'t find the wall\'s structure — no corners, edges, or furniture lines to measure against', fix: 'step back until the whole wall is in frame, corners included' });
    return {
      pass: problems.length === 0,
      level: problems.length === 0 ? (caveats.length ? 'readable with caveats' : 'readable') : 'retake',
      caveats, problems, scores: s
    };
  }

  /* ————— roll door: file input done right ————— */
  let heicReady = null;
  function ensureHeic() {
    if (window.heic2any) return Promise.resolve();
    if (heicReady) return heicReady;
    heicReady = new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
    return heicReady;
  }

  function openRoll(opts) {
    opts = opts || {};
    return new Promise(resolve => {
      const inp = document.createElement('input');
      inp.type = 'file';
      inp.accept = 'image/*';           // no capture attribute — the roll stays reachable
      if (opts.multiple) inp.multiple = true;
      inp.onchange = async () => {
        const files = [...inp.files];
        if (!files.length) return resolve(null);
        const out = [];
        for (const f of files.slice(0, 2)) {   // wide + optional detail
          try {
            if (/image\/hei[cf]/i.test(f.type) || /\.hei[cf]$/i.test(f.name || '')) await ensureHeic();
            const canvas = await window.GLUE.loadImageFile(f);
            out.push({ canvas, blob: f, gate: gate(canvas) });
          } catch (e) {
            out.push({ error: e && e.message === 'heic' ? 'heic' : 'unreadable', name: f.name });
          }
        }
        resolve(out.length ? out : null);
      };
      // cancel detection: focus returns with no change
      inp.oncancel = () => resolve(null);
      inp.click();
    });
  }

  /* ————— camera door: coached live capture ————— */
  function openCamera(opts) {
    opts = opts || {};
    return new Promise(async resolve => {
      /* permission state → three different screens */
      let perm = 'prompt';
      try {
        if (navigator.permissions && navigator.permissions.query)
          perm = (await navigator.permissions.query({ name: 'camera' })).state;
      } catch (e) {}
      if (perm === 'denied') { resolve({ denied: true }); return; }

      const wrap = document.createElement('div');
      wrap.style.cssText = 'position:fixed;inset:0;z-index:120;background:#0d0b09;display:grid;place-items:center;color:#F6F1E7;font-family:Manrope,sans-serif';
      wrap.innerHTML = `
        <div style="position:absolute;inset:0">
          <video autoplay playsinline muted style="width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity .4s"></video>
          <canvas style="position:absolute;inset:0;width:100%;height:100%"></canvas>
        </div>
        <div class="ic-card" style="position:relative;max-width:24rem;text-align:center;padding:1.5rem">
          <div style="font-family:Fraunces,serif;font-size:1.5rem;margin-bottom:.7rem">Point at the wall <em style="color:#B08D57">you want styled.</em></div>
          <p style="font-size:.86rem;color:rgba(246,241,231,.65);line-height:1.65;margin-bottom:1.3rem">I'll coach the shot — steady, bright, whole wall in frame — and take it myself the moment it's right. Nothing is uploaded until you say so.</p>
          <button class="ic-go" style="padding:.85rem 1.7rem;border-radius:999px;background:#F6F1E7;color:#14100C;font-weight:800;font-size:.74rem;letter-spacing:.1em;text-transform:uppercase;border:0;cursor:pointer">Open the camera</button>
          <div><button class="ic-cancel" style="margin-top:1rem;background:none;border:0;color:rgba(246,241,231,.5);font-size:.72rem;cursor:pointer">use a photo from my roll instead</button></div>
        </div>
        <div class="ic-hud" style="position:absolute;left:0;right:0;bottom:0;padding:1rem 1.2rem calc(1.4rem + env(safe-area-inset-bottom));display:none;text-align:center">
          <div class="ic-coach" style="min-height:1.6em;font-size:.9rem;font-weight:700;margin-bottom:.9rem;text-shadow:0 2px 8px rgba(0,0,0,.6)"></div>
          <button class="ic-shut" style="width:68px;height:68px;border-radius:50%;border:4px solid #F6F1E7;background:rgba(246,241,231,.15);cursor:pointer"></button>
          <div><button class="ic-cancel2" style="margin-top:.8rem;background:none;border:0;color:rgba(246,241,231,.55);font-size:.72rem;cursor:pointer">cancel</button></div>
        </div>`;
      document.body.appendChild(wrap);
      const vid = wrap.querySelector('video');
      const hud = wrap.querySelector('.ic-hud');
      const coach = wrap.querySelector('.ic-coach');
      let stream = null, alive = true, best = null, okStreak = 0, resolved = false;

      const done = val => { if (resolved) return; resolved = true; alive = false;
        if (stream) stream.getTracks().forEach(t => t.stop());
        wrap.remove(); resolve(val); };
      wrap.querySelector('.ic-cancel').onclick = () => done(null);
      wrap.querySelector('.ic-cancel2').onclick = () => done(null);

      wrap.querySelector('.ic-go').onclick = async () => {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        } catch (e) { done({ denied: true }); return; }
        vid.srcObject = stream;
        wrap.querySelector('.ic-card').style.display = 'none';
        hud.style.display = 'block';
        vid.style.opacity = 1;
        coachLoop();
      };
      wrap.querySelector('.ic-shut').onclick = () => captureFrame(true);

      function grab() {
        if (!vid.videoWidth) return null;
        const c = document.createElement('canvas');
        c.width = vid.videoWidth; c.height = vid.videoHeight;
        c.getContext('2d').drawImage(vid, 0, 0);
        return c;
      }
      function captureFrame(manual) {
        const c = best && !manual ? best.canvas : grab();
        if (!c) return;
        done({ canvas: c, blob: null, gate: gate(c), live: true });
      }
      function coachLoop() {
        if (!alive) return;
        const c = grab();
        if (c) {
          const s = scoreFrame(c);
          const msgs = [];
          if (s.blurVar < 100) msgs.push('hold steady — the shot is soft');
          if (s.exposure.mean < 40) msgs.push('too dark — face the window or turn on a lamp');
          if (s.exposure.hi > 0.18) msgs.push('step out of the glare — the wall is blowing out');
          if (s.density < 2.0) msgs.push('step back — I need the whole wall, corners included');
          coach.textContent = msgs[0] || (s.blurVar < 260 ? 'good — one breath steadier and I\'ll take it' : 'perfect — hold it right there…');
          coach.style.color = msgs.length ? '#F6F1E7' : '#9ecfa7';
          if (!msgs.length) {
            okStreak++;
            if (!best || s.blurVar > best.score) best = { canvas: c, score: s.blurVar };
            if (okStreak >= 2 && best) { captureFrame(false); return; }   // auto-capture: bracketing, best of the run
          } else { okStreak = 0; if (!best || s.blurVar > best.score) best = { canvas: c, score: s.blurVar }; }
        }
        setTimeout(coachLoop, 320);
      }
    });
  }

  return { openRoll, openCamera, gate, scoreFrame };
})();
